import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function ListerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        phone: "",
        password: "",
        password_confirmation: "",
        father_name: "",
        gender: "",
        dob: "",
        cnic: "",
        whatsapp: "",
        house_address: "",
        account_date: "",
        reference: "",
        account_status: "1",
        account_message: "",
        opening_balance: 0,
        per_product_amount: 0,
        payment_type: "",
        payment_value: "",
        settlement_type: "payable",
        add_listing: false,
    });

    const [files, setFiles] = useState({
        cnic_front_image: null,
        cnic_back_image: null,
        user_image: null,
    });

    const [previews, setPreviews] = useState({
        cnic_front_image: null,
        cnic_back_image: null,
        user_image: null,
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.lister);
    const [alert, setAlert] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const resolveImgUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const baseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage";
        return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "multipart/form-data",
        },
    });

    const populateForm = (item) => {
        const listerProfile = item.lister || {};
        const rawStatus = listerProfile.status ?? item.status ?? listerProfile.account_status ?? item.account_status;

        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            phone: item.phone || "",
            password: "",
            password_confirmation: "",
            father_name: listerProfile.father_name || "",
            gender: listerProfile.gender || "",
            dob: listerProfile.dob || "",
            cnic: listerProfile.cnic || "",
            whatsapp: listerProfile.whatsapp || "",
            house_address: listerProfile.house_address || "",
            account_date: listerProfile.account_date || "",
            reference: listerProfile.reference || "",
            account_status: rawStatus === true || rawStatus === 1 || rawStatus === "1" ? "1" : "0",
            account_message: listerProfile.account_message || "",
            opening_balance: listerProfile.opening_balance ?? item.opening_balance ?? 0,
            per_product_amount: listerProfile.per_product_amount ?? 0,
            payment_type: listerProfile.payment_type || item.payment_type || "",
            payment_value: listerProfile.payment_value ?? item.payment_value ?? "",
            settlement_type: listerProfile.settlement_type || item.settlement_type || "payable",
            add_listing: listerProfile.add_listing ?? false,
        });

        setPreviews({
            user_image: resolveImgUrl(item.user_image || listerProfile.user_image),
            cnic_front_image: resolveImgUrl(listerProfile.cnic_front_image),
            cnic_back_image: resolveImgUrl(listerProfile.cnic_back_image),
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.lister) {
                populateForm(location.state.lister);
                setPageLoading(false);
            } else {
                const fetchLister = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/lister`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                        );
                        const listersList = Array.isArray(res.data) ? res.data : (res.data.data || []);
                        const item = listersList.find((l) => l.id === parseInt(id));

                        if (item) {
                            populateForm(item);
                        } else {
                            setAlert({ type: "danger", message: "Lister not found." });
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load lister details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchLister();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            const file = selectedFiles[0];
            setFiles((prev) => ({ ...prev, [name]: file }));
            setPreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
        }
    };

    const getPaymentValuePlaceholder = () => {
        switch (formData.payment_type) {
            case "salary":
                return "Enter salary amount (e.g. 50000)";
            case "percentage":
                return "Enter percentage value (e.g. 10 for 10%)";
            case "fixed_amount":
                return "Enter fixed amount (e.g. 2000)";
            case "per_product":
                return "Enter amount per product (e.g. 50)";
            default:
                return "Select payment type first";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                let value = formData[key];

                if (key === "account_status" || key === "add_listing") {
                    value = value === "1" || value === true || value === 1 ? "1" : "0";
                } else if (typeof value === "boolean") {
                    value = value ? "1" : "0";
                }

                payload.append(key, value);
            }
        });

        if (files.cnic_front_image) payload.append("cnic_front_image", files.cnic_front_image);
        if (files.cnic_back_image) payload.append("cnic_back_image", files.cnic_back_image);
        if (files.user_image) payload.append("user_image", files.user_image);

        let url = `${process.env.REACT_APP_API_URL}/admin/lister`;
        if (isEdit) {
            url = `${process.env.REACT_APP_API_URL}/admin/lister/${id}`;
            payload.append("_method", "PUT");
        }

        try {
            const response = await axios.post(url, payload, getAuthHeader());

            if (response.status === 200 || response.data?.success) {
                navigate("/listers");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading lister data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 p-4 rounded-4 shadow-sm border-0 bg-white d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h3 className="mc-breadcrumb-title fw-bold text-dark m-0">
                                {isEdit ? t("Edit Lister") : t("Add Lister")}
                            </h3>
                            <ul className="mc-breadcrumb-list mt-1 m-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-secondary">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/admin/lister" className="mc-breadcrumb-link text-secondary">{t("Listers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{isEdit ? t("Edit") : t("Add")}</li>
                            </ul>
                        </div>
                        <Button variant="secondary" className="rounded-3 px-4" onClick={() => navigate("/admin/lister")}>
                            {t("Back")}
                        </Button>
                    </div>
                </Col>

                <Col xl={12} className="mx-auto">
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="rounded-3 shadow-sm">
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Nickname <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                placeholder="e.g. Johnny"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nickname?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="e.g. lister@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Phone <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. 03001234567"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.phone?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Father Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="father_name"
                                                placeholder="e.g. Richard Doe"
                                                value={formData.father_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.father_name}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Gender</Form.Label>
                                            <Form.Select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                isInvalid={!!errors.gender}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Payment Type</Form.Label>
                                            <Form.Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_type}
                                            >
                                                <option value="">Select Payment Type</option>
                                                <option value="salary">Salary</option>
                                                <option value="per_product">Per Product</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.payment_type?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Payment Value</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="payment_value"
                                                placeholder={getPaymentValuePlaceholder()}
                                                value={formData.payment_value}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_value}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.payment_value?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Settlement Type</Form.Label>
                                            <Form.Select
                                                name="settlement_type"
                                                value={formData.settlement_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.settlement_type}
                                            >
                                                <option value="payable">Payable</option>
                                                <option value="receivable">Receivable</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.settlement_type?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Opening Balance</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                placeholder="0"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                                isInvalid={!!errors.opening_balance}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.opening_balance?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Date of Birth</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                                isInvalid={!!errors.dob}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">CNIC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                placeholder="e.g. 35201-1234567-1"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                                isInvalid={!!errors.cnic}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.cnic?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">WhatsApp</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="whatsapp"
                                                placeholder="e.g. 03001234567"
                                                value={formData.whatsapp}
                                                onChange={handleChange}
                                                isInvalid={!!errors.whatsapp}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Account Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="account_date"
                                                value={formData.account_date}
                                                onChange={handleChange}
                                                isInvalid={!!errors.account_date}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Status <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="account_status"
                                                value={formData.account_status}
                                                onChange={handleChange}
                                                isInvalid={!!errors.account_status}
                                                required
                                            >
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.account_status?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Reference</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="reference"
                                                placeholder="e.g. Referred by Agent"
                                                value={formData.reference}
                                                onChange={handleChange}
                                                isInvalid={!!errors.reference}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {isEdit ? "New Password (Optional)" : <>Password <span className="text-danger">*</span></>}
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Enter password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.password}
                                                    required={!isEdit}
                                                    className="pe-5"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                    style={{ transform: "translateY(-50%)" }}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? "Hide" : "Show"}
                                                </Button>
                                            </div>
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {(formData.password || !isEdit) && (
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </Form.Label>
                                                <div className="position-relative">
                                                    <Form.Control
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        name="password_confirmation"
                                                        placeholder="Re-enter password"
                                                        value={formData.password_confirmation}
                                                        onChange={handleChange}
                                                        required={!isEdit || Boolean(formData.password)}
                                                        className="pe-5"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                        style={{ transform: "translateY(-50%)" }}
                                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                                    >
                                                        {showConfirmPassword ? "Hide" : "Show"}
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    )}

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">User Image</Form.Label>
                                            <Form.Control type="file" name="user_image" onChange={handleFileChange} accept="image/*" />
                                            {previews.user_image && (
                                                <div className="mt-2 text-center p-2 border rounded-3 bg-light">
                                                    <img
                                                        src={previews.user_image}
                                                        alt="User Preview"
                                                        className="img-fluid rounded-2 shadow-sm"
                                                        style={{ maxHeight: "110px", objectFit: "cover" }}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">CNIC Front Image</Form.Label>
                                            <Form.Control type="file" name="cnic_front_image" onChange={handleFileChange} accept="image/*" />
                                            {previews.cnic_front_image && (
                                                <div className="mt-2 text-center p-2 border rounded-3 bg-light">
                                                    <img
                                                        src={previews.cnic_front_image}
                                                        alt="CNIC Front Preview"
                                                        className="img-fluid rounded-2 shadow-sm"
                                                        style={{ maxHeight: "110px", objectFit: "cover" }}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">CNIC Back Image</Form.Label>
                                            <Form.Control type="file" name="cnic_back_image" onChange={handleFileChange} accept="image/*" />
                                            {previews.cnic_back_image && (
                                                <div className="mt-2 text-center p-2 border rounded-3 bg-light">
                                                    <img
                                                        src={previews.cnic_back_image}
                                                        alt="CNIC Back Preview"
                                                        className="img-fluid rounded-2 shadow-sm"
                                                        style={{ maxHeight: "110px", objectFit: "cover" }}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">House Address</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="house_address"
                                                placeholder="e.g. House #123, Street 4, Sector B..."
                                                value={formData.house_address}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Account Message</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="account_message"
                                                placeholder="Add any account note..."
                                                value={formData.account_message}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Check
                                            type="checkbox"
                                            id="add_listing"
                                            name="add_listing"
                                            label="Allow Add Listing"
                                            checked={formData.add_listing}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                                    <Button variant="secondary" className="px-4 border" onClick={() => navigate("/admin/lister")}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" className="px-4" disabled={submitting}>
                                        {submitting ? "Saving..." : isEdit ? "Update Lister" : "Save Lister"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}